import { formatMoney } from '@theme/money-formatting';

const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/i;

// The audited reward collection is unpublished, so public collection handles
// cannot prove nonmembership. Evaluate its verified rule only when configured.
export function builderRewardEligibility(product, collections, rule) {
    if (rule === 'positive-product-price') {
        if (!Array.isArray(product?.variants) || !product.variants.length) return null;
        if (product.variants.some(variant => Number.isSafeInteger(variant.price) && variant.price > 0)) return true;
        return product.variants.every(variant => Number.isSafeInteger(variant.price) && variant.price === 0) ? false : null;
    }
    return Array.isArray(collections) && collections.includes('frest-rewards-all-products') ? true : null;
}

// Search is discovery only; a full native product response must pass these checks.
export function normalizeBuilderArtwork(product, { origin, root, moneyFormat, currency }) {
    if (!/^[A-Z]{3}$/.test(currency || '') || typeof moneyFormat !== 'string' || !moneyFormat) throw new TypeError('Invalid pricing context');
    if (!Number.isSafeInteger(product?.id) || product.id <= 0 || !HANDLE.test(product.handle || '')
        || typeof product.title !== 'string' || !product.title.trim()
        || String(product.type ?? product.product_type ?? '').trim().toLowerCase() !== 'art print'
        || product.available !== true || product.requires_selling_plan === true
        || (Array.isArray(product.selling_plan_groups) && product.selling_plan_groups.length))
        throw new TypeError('Ineligible artwork');
    const tags = Array.isArray(product.tags) ? product.tags : typeof product.tags === 'string' ? product.tags.split(',') : [];
    const normalized = tags.map(tag => String(tag).trim().toLowerCase().replace(/[_\s]+/g, '-'));
    if (normalized.some(tag => tag.includes('calendar') || ['frest-bundle-parent', 'frest-art-set-parent', 'fulfillment-pending'].includes(tag)))
        throw new TypeError('Excluded artwork');
    const variants = (product.variants || []).filter(variant => {
        if (!Number.isSafeInteger(variant.id) || variant.id <= 0 || variant.available !== true || variant.requires_shipping !== true
            || !Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.title !== 'string' || !variant.title.trim()
            || variant.requires_selling_plan === true || variant.selling_plan_allocations?.length) return false;
        const rule = variant.quantity_rule;
        if (rule && (rule.min !== 1 || rule.increment !== 1 || (rule.max != null && rule.max < 1))) return false;
        return true;
    }).map(variant => {
        const money = document.createElement('template');
        money.innerHTML = formatMoney(variant.price, moneyFormat, currency);
        return { id: variant.id, title: variant.title, price: variant.price, priceText: money.content.textContent };
    });
    if (!variants.length || new Set(variants.map(variant => variant.id)).size !== variants.length
        || new Set(variants.map(variant => variant.title)).size !== variants.length) throw new TypeError('No unambiguous sizes');
    const productUrl = new URL(`products/${encodeURIComponent(product.handle)}`, root);
    if (productUrl.origin !== origin) throw new TypeError('Invalid artwork origin');
    const imageSource = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src ?? product.featured_image?.url;
    if (!imageSource) throw new TypeError('Missing artwork image');
    const image = new URL(imageSource, origin);
    if (image.protocol !== 'https:' || image.username || image.password
        || (image.origin !== origin && image.hostname !== 'cdn.shopify.com')) throw new TypeError('Invalid artwork image');
    return { productId: product.id, url: productUrl.href, title: product.title, imageUrl: image.href, imageAlt: product.title, variants, tags };
}

export function storefrontRoot(origin, route = '/') {
    const root = new URL(route, origin);
    if (root.origin !== origin || root.search || root.hash || root.username || root.password) throw new TypeError('Invalid storefront route');
    if (!root.pathname.endsWith('/')) root.pathname += '/';
    return root;
}

export function previewThemeId(href, theme) {
    // Bind preview requests to this document's theme, not a cookie another tab can change.
    if (theme?.role === 'main') return null;
    const valid = value => /^[1-9]\d*$/.test(String(value ?? '')) && Number.isSafeInteger(Number(value));
    const explicit = new URL(href).searchParams.get('preview_theme_id');
    if (valid(explicit)) return String(explicit);
    if (['unpublished', 'development'].includes(theme?.role) && valid(theme?.id)) return String(theme.id);
    return null;
}

export function discoveryArtwork(product, origin) {
    const id = Number(product?.id);
    const type = String(product?.type ?? product?.product_type ?? '').trim().toLowerCase();
    if (!Number.isSafeInteger(id) || id <= 0 || !HANDLE.test(product?.handle || '')
        || typeof product.title !== 'string' || !product.title.trim() || (type && type !== 'art print')) return null;
    const tags = Array.isArray(product.tags) ? product.tags : typeof product.tags === 'string' ? product.tags.split(',') : [];
    if (tags.some(tag => {
        const normalized = String(tag).trim().toLowerCase().replace(/[_\s]+/g, '-');
        return normalized.includes('calendar') || ['fulfillment-pending', 'frest-bundle-parent', 'frest-art-set-parent'].includes(normalized);
    }) || product.available === false || product.requires_selling_plan === true || product.selling_plan_groups?.length) return null;
    const source = typeof product.image === 'string' ? product.image : product.image?.url
        ?? (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url ?? product.featured_image?.src);
    if (!source) return null;
    let image;
    try { image = new URL(source, origin); }
    catch { return null; }
    if (image.protocol !== 'https:' || image.username || image.password || (image.origin !== origin && image.hostname !== 'cdn.shopify.com')) return null;
    image.searchParams.set('width', '480');
    return { id, handle: product.handle, title: product.title, image: image.href,
        priceText: typeof product.priceText === 'string' ? product.priceText : '', priceVaries: product.priceVaries === true };
}

const SIGNAL_GROUPS = [
    ['Cats', 'cat cats kitten kittens feline felines'], ['Dogs', 'dog dogs puppy puppies canine'],
    ['Moon', 'moon moons lunar'], ['Stars', 'star stars celestial astronomy astronomical meteor comet constellation'],
    ['Sun', 'sun suns solar'], ['Flowers', 'flower flowers floral botanical botanicals lily lilies'],
    ['Birds', 'bird birds owl owls crow crows raven ravens magpie magpies'], ['Tigers', 'tiger tigers'],
    ['Rabbits', 'rabbit rabbits bunny bunnies hare hares'], ['Foxes', 'fox foxes'],
    ['Fish', 'fish koi goldfish'], ['Frogs', 'frog frogs toad toads'], ['Mushrooms', 'mushroom mushrooms fungi'],
    ['Angels', 'angel angels seraph seraphim cherub cherubim'], ['Sacred', 'sacred saint saints madonna christ jesus religious'],
    ['Witches', 'witch witches witchcraft'], ['Skulls', 'skull skulls skeleton skeletons'],
    ['Dragons', 'dragon dragons'], ['Fairy tales', 'fairy fairies fairytale fairytales'],
    ['Ocean', 'ocean sea seaside coastal shore'], ['Forest', 'forest woodland woods tree trees'],
    ['Woodcut', 'woodcut woodcuts woodblock woodblocks'], ['Japanese', 'japanese japan ukiyo'],
    ['Korean', 'korean korea minhwa joseon'], ['Medieval', 'medieval marginalia illuminated'],
    ['Gothic', 'gothic goth'], ['Folk art', 'folk naive'], ['Symbolism', 'symbolism symbolist'],
    ['Art Nouveau', 'nouveau'], ['Impressionist', 'impressionist impressionism'], ['Surreal', 'surreal surrealism'],
    ['Dark', 'dark moody macabre'], ['Whimsical', 'whimsical playful'], ['Dreamy', 'dreamy dream dreamlike'],
];
const DEFAULT_TERMS = ['Cats', 'Moon', 'Flowers', 'Woodcut', 'Gothic', 'Fairy tales'];
const GENERIC_TERMS = new Set('art print prints wall decor vintage antique poster posters painting artwork artworks gift gifts present bedroom living room kitchen house home gallery edited export import batch fulfillment pending parent bundle calendar custom made'.split(' '));

function artworkTags(product) {
    return Array.isArray(product?.tags) ? product.tags : typeof product?.tags === 'string' ? product.tags.split(',') : [];
}

/** Text metadata provides discovery hints only; it never changes product eligibility. */
export function artworkSearchTerms(products, limit = 8) {
    const scores = new Map();
    for (const product of products.filter(Boolean)) {
        const local = new Map();
        const sources = [{ text: product.title || '', weight: 3 }, ...(Array.isArray(product.collections) ? product.collections : []).map(handle => ({ text: String(handle).replaceAll('-', ' '), weight: 2 }))];
        for (const tag of artworkTags(product)) {
            const normalized = String(tag).trim().toLowerCase().replace(/[_\s]+/g, '-');
            if (/^(edited\d*|export|import|batch|fulfillment|frest-bundle|frest-art-set|gift)(-|$)/.test(normalized)) continue;
            const recommendation = normalized.startsWith('rec-');
            const text = normalized.replace(/^rec-(subject-|style-|mood-|color-)?/, '').replaceAll('-', ' ');
            sources.push({ text, weight: recommendation ? 6 : 2 });
            if (recommendation && !/^rec-color-/.test(normalized)) {
                const words = text.split(/\s+/).filter(Boolean);
                if (words.length && words.length <= 4 && words.every(word => /^[a-z]+$/.test(word) && !GENERIC_TERMS.has(word))
                    && !SIGNAL_GROUPS.some(([, synonyms]) => words.some(word => synonyms.split(' ').includes(word)))) {
                    const term = text.charAt(0).toUpperCase() + text.slice(1);
                    local.set(term, Math.max(local.get(term) || 0, 4));
                }
            }
        }
        for (const { text, weight } of sources) {
            const words = new Set(text.toLowerCase().split(/[^a-z]+/).filter(Boolean));
            for (const [term, synonyms] of SIGNAL_GROUPS) {
                if (synonyms.split(' ').some(word => words.has(word))) local.set(term, Math.max(local.get(term) || 0, weight));
            }
        }
        for (const [term, score] of local) scores.set(term, (scores.get(term) || 0) + score);
    }
    return [...scores].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([term]) => term);
}

export function otherSlotArtworks(ids, activeSlot, records) {
    return ids.flatMap((id, index) => index !== activeSlot && records.has(id) ? [records.get(id)] : []);
}

export function suggestedArtworkQuery(terms) {
    return terms.slice(0, 3).map(term => term.includes(' ') ? `"${term}"` : term).join(' OR ');
}

export function shuffleArtworks(products, random = Math.random) {
    const shuffled = [...products];
    for (let index = shuffled.length - 1; index > 0; index--) {
        const target = Math.floor(random() * (index + 1));
        [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
    }
    return shuffled;
}

export function rankSuggestedArtworks(products, terms) {
    const wanted = new Set(terms);
    return products.map((product, index) => ({ product, index,
        score: artworkSearchTerms([product], 20).reduce((total, term) => total + (wanted.has(term) ? 1 : 0), 0) }))
        .sort((a, b) => b.score - a.score || a.index - b.index).map(item => item.product);
}

class FrestBuildSet extends HTMLElement {
    #lifecycle;
    #builder;
    #root;
    #dialog;
    #search;
    #results;
    #status;
    #more;
    #retry;
    #count;
    #returnFocus;
    #activeSlot = null;
    #loadingSlot = null;
    #generation = 0;
    #controller;
    #timer;
    #busy = false;
    #selecting = false;
    #query = '';
    #nextPage = 1;
    #retryPage = 1;
    #retryAppend = false;
    #seen = new Set();
    #selectedProducts = [];
    #records = new Map();
    #terms = DEFAULT_TERMS;
    #collection = '';
    #automatic = false;
    #randomStart = null;
    #randomWrapped = false;
    #recommendationFallback = false;

    async connectedCallback() {
        this.#lifecycle?.abort();
        this.#lifecycle = new AbortController();
        const { signal } = this.#lifecycle;
        this.#builder = this.querySelector('frest-art-set[data-art-set-builder]');
        this.#dialog = this.querySelector('[data-build-dialog]');
        this.#search = this.querySelector('[data-build-search]');
        this.#results = this.querySelector('[data-build-results]');
        this.#status = this.querySelector('[data-build-status]');
        this.#more = this.querySelector('[data-build-more]');
        this.#retry = this.querySelector('[data-build-retry]');
        this.#count = this.querySelector('[data-build-result-count]');
        try { this.#root = storefrontRoot(window.location.origin, window.Shopify?.routes?.root ?? '/'); }
        catch { return; }
        await customElements.whenDefined('frest-art-set');
        if (signal.aborted || !this.#builder || typeof this.#builder.clearArtwork !== 'function'
            || !this.#dialog || !this.#search || !this.#results || !this.#status || !this.#more || !this.#retry || !this.#count) return;
        this.#syncSelections();
        this.addEventListener('click', this.#onClick, { signal });
        this.addEventListener('frest:art-set-selection', event => {
            this.#syncSelections();
            this.#refreshButtons();
        }, { signal });
        this.#dialog.addEventListener('cancel', event => { event.preventDefault(); this.#dialog.close(); }, { signal });
        this.#dialog.addEventListener('close', () => {
            this.#cancelWork();
            this.#activeSlot = null;
            if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
        }, { signal });
        this.#search.addEventListener('input', event => { if (!event.isComposing) this.#queueSearch(); }, { signal });
        this.#search.addEventListener('compositionend', () => this.#queueSearch(), { signal });
        this.#search.addEventListener('keydown', event => {
            if (event.key === 'Enter') { event.preventDefault(); this.#queueSearch(0); }
        }, { signal });
    }

    disconnectedCallback() {
        this.#lifecycle?.abort();
        this.#cancelWork();
        if (this.#dialog?.open) this.#dialog.close();
        this.#activeSlot = null;
    }

    #message(key, fallback) { return this.dataset[key] || fallback; }

    #onClick = event => {
        const button = event.target instanceof Element ? event.target.closest('button') : null;
        if (!button || button.disabled || !this.contains(button)) return;
        if (button.hasAttribute('data-build-open')) {
            this.#open(Number(button.dataset.buildOpen), button);
        } else if (button.hasAttribute('data-build-clear')) {
            const index = Number(button.dataset.buildClear);
            if (!Number.isInteger(index) || index < 0 || index > 3) return;
            this.#builder.clearArtwork(index);
            this.querySelector(`[data-build-slot="${index}"] [data-art-set-empty] [data-art-set-open]`)?.focus();
        } else if (button.hasAttribute('data-build-term')) {
            this.#search.value = button.dataset.buildTerm || '';
            this.#queueSearch(0);
        } else if (button.hasAttribute('data-build-collection')) {
            const handle = button.dataset.buildCollection || '';
            if (handle && (!HANDLE.test(handle) || /custom[-_ ]?made/i.test(handle))) return;
            this.#cancelWork();
            this.#collection = this.#collection === handle ? '' : handle;
            this.#query = ''; this.#search.value = ''; this.#automatic = false; this.#recommendationFallback = false;
            this.#randomStart = null; this.#randomWrapped = false;
            this.#nextPage = 1;
            if (!this.#collection) this.#defaultDiscovery();
            this.#refreshDiscovery();
            this.#results.replaceChildren(); this.#seen.clear(); this.#count.textContent = '';
            this.#loadCatalog(this.#randomStart || 1, false);
        } else if (button.hasAttribute('data-build-close')) this.#dialog.close();
        else if (button.hasAttribute('data-build-more')) {
            if (!this.#busy && this.#nextPage) this.#loadCatalog(this.#nextPage, true);
        } else if (button.hasAttribute('data-build-retry')) {
            this.#loadCatalog(this.#retryPage, this.#retryAppend);
        } else if (button.dataset.buildHandle) this.#choose(button.dataset.buildHandle);
    };

    #open(index, button) {
        if (!Number.isInteger(index) || index < 0 || index > 3) return;
        this.#cancelWork();
        this.#activeSlot = index;
        this.#returnFocus = button;
        this.#collection = '';
        this.#search.value = '';
        this.#defaultDiscovery();
        this.#results.replaceChildren();
        this.#seen = new Set();
        this.#nextPage = 1;
        this.#retry.hidden = true;
        this.#dialog.showModal();
        this.#search.focus();
        this.#loadCatalog(this.#randomStart || 1, false);
    }

    #syncSelections() {
        this.#selectedProducts = [...this.#builder.querySelectorAll('[data-art-set-product]')].map(select => {
            const id = Number(select.value) || null;
            if (id && !this.#records.has(id)) {
                const registered = this.#builder.getRegisteredArtwork?.(id);
                this.#records.set(id, { id, title: registered?.title || select.selectedOptions?.[0]?.textContent || '', tags: registered?.tags || [] });
            }
            return id;
        });
    }

    #defaultDiscovery() {
        this.#syncSelections();
        const selected = otherSlotArtworks(this.#selectedProducts, this.#activeSlot, this.#records);
        const tailored = selected.length ? artworkSearchTerms(selected) : [];
        this.#terms = tailored.length ? tailored : DEFAULT_TERMS;
        this.#automatic = tailored.length > 0;
        this.#recommendationFallback = false;
        this.#query = this.#automatic ? suggestedArtworkQuery(this.#terms) : '';
        this.#randomWrapped = false;
        const pages = Math.max(1, Math.ceil((Number(this.dataset.buildCatalogCount) || 24) / 24));
        this.#randomStart = this.#automatic ? null : 1 + Math.floor(Math.random() * pages);
        this.#refreshDiscovery();
    }

    #refreshDiscovery() {
        const container = this.querySelector('[data-build-terms]');
        if (container) container.replaceChildren(...this.#terms.map(term => {
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'frest-build-set__term'; button.dataset.buildTerm = term;
            button.textContent = term;
            button.setAttribute('aria-pressed', String(!this.#automatic && this.#query.toLowerCase() === term.toLowerCase()));
            return button;
        }));
        for (const button of this.querySelectorAll('[data-build-collection]')) {
            button.setAttribute('aria-pressed', String(Boolean(this.#collection) && button.dataset.buildCollection === this.#collection));
        }
        const context = this.querySelector('[data-build-context]');
        if (context) context.textContent = this.#recommendationFallback ? this.#message('sparseContextMessage', 'No close matches yet. Explore more artworks or choose a search or collection.')
            : this.#automatic ? this.#message('discoveryContextMessage', 'Inspired by your selected artworks')
            : this.#query ? this.#message('searchContextMessage', 'Search results')
            : this.#collection ? this.#message('collectionContextMessage', 'Artworks in this collection')
            : this.#message('randomContextMessage', 'Explore a fresh mix of artworks');
    }

    #cancelWork() {
        ++this.#generation;
        clearTimeout(this.#timer);
        this.#controller?.abort();
        if (this.#loadingSlot !== null) this.#builder?.setSlotLoading(this.#loadingSlot, false);
        this.#loadingSlot = null;
        this.#busy = false;
        this.#selecting = false;
        this.#results?.removeAttribute('aria-busy');
    }

    #startWork(selecting = false) {
        this.#cancelWork();
        this.#busy = true;
        this.#selecting = selecting;
        this.#loadingSlot = this.#activeSlot;
        this.#builder.setSlotLoading(this.#activeSlot, true);
        this.#results.setAttribute('aria-busy', 'true');
        this.#retry.hidden = true;
        this.#refreshButtons();
        return this.#generation;
    }

    #finishWork(generation) {
        if (generation !== this.#generation || this.#lifecycle.signal.aborted) return;
        if (this.#loadingSlot !== null) this.#builder.setSlotLoading(this.#loadingSlot, false);
        this.#loadingSlot = null;
        this.#busy = false;
        this.#selecting = false;
        this.#results.removeAttribute('aria-busy');
        this.#refreshButtons();
    }

    #refreshButtons() {
        if (!this.#more || !this.#retry || !this.#results) return;
        this.#more.hidden = this.#query.length === 1 || this.#nextPage === null;
        this.#more.disabled = this.#busy;
        this.#retry.disabled = this.#busy;
        for (const button of this.#results.querySelectorAll('[data-build-handle]')) {
            const productId = Number(button.dataset.productId);
            const duplicate = this.#selectedProducts.some((id, index) => index !== this.#activeSlot && id === productId);
            button.dataset.selected = String(duplicate);
            button.disabled = this.#selecting || duplicate;
            if (duplicate) button.setAttribute('aria-label', `${button.dataset.buildTitle || ''}, ${this.#message('alreadySelectedMessage', 'Already selected')}`);
            else button.removeAttribute('aria-label');
        }
    }

    #url(path) {
        const url = new URL(path, this.#root);
        if (url.origin !== window.location.origin) throw new TypeError('Invalid request origin');
        const previewTheme = previewThemeId(window.location.href, window.Shopify?.theme);
        if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
        return url;
    }

    async #fetch(url) {
        if (url.origin !== window.location.origin) throw new TypeError('Invalid request origin');
        const controller = new AbortController();
        this.#controller = controller;
        const lifecycle = this.#lifecycle.signal;
        const cancel = () => controller.abort();
        lifecycle.addEventListener('abort', cancel, { once: true });
        const timeout = setTimeout(cancel, 10000);
        try {
            if (lifecycle.aborted) controller.abort();
            // Alternate Liquid views are HTML routes whose bodies contain our JSON.
            const accept = url.searchParams.get('view') === 'frest-set-catalog' ? 'text/html' : 'application/json';
            const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: accept } });
            if (!response.ok || (response.url && new URL(response.url).origin !== window.location.origin)) throw new Error('Artwork request failed');
            return await response.json();
        } finally { clearTimeout(timeout); lifecycle.removeEventListener('abort', cancel); }
    }

    #isCurrent(generation) { return generation === this.#generation && !this.#lifecycle.signal.aborted && this.#dialog.open; }

    #render(products, append) {
        if (!Array.isArray(products)) throw new TypeError('Invalid catalogue');
        if (!append) { this.#results.replaceChildren(); this.#seen.clear(); }
        const cards = [];
        const arranged = this.#automatic ? rankSuggestedArtworks(products, this.#terms)
            : this.#randomStart !== null ? shuffleArtworks(products) : products;
        for (const record of arranged.slice(0, 72)) {
            const product = discoveryArtwork(record, window.location.origin);
            if (!product || this.#seen.has(product.id)) continue;
            this.#seen.add(product.id);
            this.#records.set(product.id, { id: product.id, title: product.title, tags: artworkTags(record),
                collections: Array.isArray(record.collections) ? record.collections : null });
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'frest-build-set__catalog-card';
            button.dataset.buildHandle = product.handle; button.dataset.productId = String(product.id); button.dataset.buildTitle = product.title;
            const image = document.createElement('img');
            image.src = product.image; image.alt = ''; image.width = 480; image.height = 480; image.loading = 'lazy';
            const title = document.createElement('span'); title.className = 'frest-build-set__catalog-title'; title.textContent = product.title;
            button.append(image, title);
            if (product.priceText) {
                const price = document.createElement('span'); price.className = 'frest-build-set__catalog-price';
                price.textContent = product.priceVaries ? this.#message('fromPriceMessage', 'From __PRICE__').replace('__PRICE__', product.priceText) : product.priceText;
                button.append(price);
            }
            cards.push(button);
        }
        this.#results.append(...cards);
        this.#count.textContent = this.#message('countMessage', '__COUNT__ artworks shown').replace('__COUNT__', String(this.#seen.size));
        this.#refreshButtons();
        return cards.length;
    }

    async #loadCatalog(page, append) {
        if (!this.#dialog.open || !Number.isSafeInteger(page) || page < 1) return;
        const generation = this.#startWork();
        this.#retryPage = page; this.#retryAppend = append;
        this.#status.textContent = this.#message('searchingMessage', 'Loading artworks…');
        try {
            let url = this.#url(this.#query ? 'search' : `collections/${this.#collection || 'all'}`);
            if (this.#query) {
                url.searchParams.set('q', this.#query);
                url.searchParams.set('type', 'product');
                url.searchParams.set('options[unavailable_products]', 'hide');
            }
            url.searchParams.set('view', 'frest-set-catalog'); url.searchParams.set('page', String(page));
            const records = [];
            let nextPage = null;
            let wrapped = this.#randomWrapped;
            let randomStart = this.#randomStart;
            let fallback = false;
            // Fill sparse raw catalogue pages without unbounded browsing or skipping eligible products.
            for (let request = 0; request < 3; request++) {
                url.searchParams.set('page', String(page));
                const payload = await this.#fetch(url);
                if (!this.#isCurrent(generation)) return;
                if (payload.page !== page || !(payload.nextPage === null || payload.nextPage === page + 1)
                    || !Array.isArray(payload.products) || payload.products.length > 24) throw new TypeError('Invalid catalogue page');
                const eligible = payload.products.filter(product => discoveryArtwork(product, window.location.origin));
                records.push(...eligible);
                nextPage = payload.nextPage;
                if (request === 0 && !append && this.#automatic && this.#query && !records.length && nextPage === null) {
                    fallback = true;
                    url = this.#url('collections/all'); url.searchParams.set('view', 'frest-set-catalog');
                    randomStart = 1; wrapped = false; page = 1;
                    continue;
                }
                if (randomStart !== null) {
                    if (wrapped && (nextPage === null || nextPage >= randomStart)) nextPage = null;
                    else if (nextPage === null && randomStart > 1) { wrapped = true; nextPage = 1; }
                }
                if (append || randomStart === null || records.length >= 24 || nextPage === null || request === 2) break;
                if (request === 0 && page > 1 && !records.length) {
                    // Start at page one after an empty random page, retaining the complete browse sequence.
                    randomStart = 1; wrapped = false; page = 1;
                } else page = nextPage;
            }
            if (fallback) {
                this.#query = ''; this.#recommendationFallback = true; this.#refreshDiscovery();
            }
            this.#nextPage = nextPage;
            this.#randomStart = randomStart;
            this.#randomWrapped = wrapped;
            const added = this.#render(records, append);
            this.#status.textContent = !this.#seen.size && this.#nextPage === null ? this.#message('noResultsMessage', 'No matching artworks. Try another search.')
                : this.#nextPage === null ? this.#query ? this.#message('searchEndMessage', 'All matching artworks loaded.') : this.#message('endMessage', 'All available artworks loaded.')
                : !added ? this.#message('emptyPageMessage', 'No artworks on this page. Load more to keep browsing.') : '';
            if (!append) this.querySelector('[data-build-scroll]')?.scrollTo({ top: 0 });
        } catch {
            if (this.#isCurrent(generation)) {
                this.#status.textContent = this.#message('searchErrorMessage', 'We could not load artworks. Please try again.');
                this.#retry.hidden = false;
            }
        } finally { this.#finishWork(generation); }
    }

    #queueSearch(delay = 300) {
        if (!this.#dialog.open) return;
        this.#cancelWork();
        this.#collection = '';
        this.#automatic = false; this.#recommendationFallback = false; this.#randomStart = null; this.#randomWrapped = false;
        this.#query = this.#search.value.trim();
        if (!this.#query) this.#defaultDiscovery();
        this.#refreshDiscovery();
        this.#results.replaceChildren(); this.#seen.clear(); this.#count.textContent = '';
        this.#retry.hidden = true; this.#status.textContent = '';
        this.#nextPage = 1;
        if (this.#query.length === 1) {
            this.#status.textContent = this.#message('shortMessage', 'Enter at least 2 characters.');
            this.#refreshButtons(); return;
        }
        this.#busy = true;
        this.#loadingSlot = this.#activeSlot;
        this.#builder.setSlotLoading(this.#activeSlot, true);
        this.#refreshButtons();
        const generation = this.#generation;
        this.#timer = setTimeout(() => {
            if (!this.#isCurrent(generation)) return;
            this.#loadCatalog(this.#randomStart || 1, false);
        }, delay);
    }

    async #choose(handle) {
        if (!this.#dialog.open || !HANDLE.test(handle || '') || this.#activeSlot === null) return;
        const index = this.#activeSlot;
        const generation = this.#startWork(true);
        this.#status.textContent = this.#message('loadingMessage', 'Checking this artwork and its sizes…');
        let selected = false;
        try {
            const payload = await this.#fetch(this.#url(`products/${encodeURIComponent(handle)}.js`));
            if (!this.#isCurrent(generation) || index !== this.#activeSlot) return;
            if (payload.handle !== handle) throw new TypeError('Mismatched product');
            if (this.#selectedProducts.some((id, slot) => slot !== index && id === payload.id)) throw new TypeError('Artwork already selected');
            const product = normalizeBuilderArtwork(payload, { origin: window.location.origin, root: this.#root,
                moneyFormat: this.#builder.dataset.moneyFormat, currency: this.#builder.dataset.currency });
            const collections = this.#records.get(product.productId)?.collections;
            product.rewardEligible = builderRewardEligibility(payload, collections, this.#builder.dataset.dealBogoRule);
            this.#builder.registerArtwork(product, index);
            this.#records.set(product.productId, { id: product.productId, title: product.title, tags: artworkTags(payload),
                collections: this.#records.get(product.productId)?.collections || [] });
            selected = true;
        } catch {
            if (this.#isCurrent(generation)) this.#status.textContent = this.#message('productErrorMessage', 'This artwork is unavailable for a set. Choose another.');
        } finally {
            this.#finishWork(generation);
            if (selected && this.#isCurrent(generation)) {
                this.#dialog.close();
                this.querySelector(`[data-build-slot="${index}"] [data-art-set-size]`)?.focus();
            }
        }
    }
}

if (!customElements.get('frest-build-set')) customElements.define('frest-build-set', FrestBuildSet);
